import { Box, VStack } from '@cdl/heartbeat-react';
import {
  PropsWithChildren,
  UIEvent,
  useCallback,
  useMemo,
  useReducer,
  useState,
  WheelEvent,
} from 'react';
import { useSelector } from 'react-redux';
import { squareToPixel } from '../../../plot/utils/graph.utils';
import {
  baseClustersSelector,
  clusterHorizontalLengthSelector,
  clusterVerticalRangeSelector,
} from '../../selectors/cluster.selectors';
import {
  BaseCluster,
  CONTAINER_HEIGHT,
  CONTAINER_WIDTH,
  MARGIN_RIGHT,
} from './BaseCluster';
import { BaseClustersHeader } from './BaseClustersHeader';
import { WebGLContainer } from '../../../plot/components/WebGLContainer';
import {
  dimensionReducer,
  measureDimension,
} from '../../../plot/utils/web-gl.util';
import { filterOptionSelector } from '../../../layout/selectors/layout.selectors';
import { FilterOption } from '../../../layout/types/layout-state.type';
import { Cluster } from '../../clustering.types';

const MIN_HEIGHT = squareToPixel(1);
const PADDING = 16;
const WIDTH = window.innerWidth; //window.outerWidth;
const CLUSTER_WIDTH = CONTAINER_WIDTH + MARGIN_RIGHT;

export const BaseClustersContainer: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [dimensions, dispatchToReducer] = useReducer(dimensionReducer, {});
  const filterOption = useSelector(filterOptionSelector);
  const clusters = useSelector(baseClustersSelector);
  const [horizontalScroll, setHorizontalScroll] = useState(0);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const xMax = useSelector(clusterHorizontalLengthSelector(clusters[0]?.id));
  const { yMin, yMax } = useSelector(clusterVerticalRangeSelector);
  const [oldFilter, setOldFilter] = useState<FilterOption>('All');
  const [oldClusters, setOldClusters] = useState<Cluster[]>([]);

  /**
   * - [] With extra height on morphology container the clusters scroll
   *  vertically
   */
  const memoisedBaseClusters = useMemo(() => {
    // initialize the dimensions because a cluster
    // has been added / deleted so we need their new positions
    // in the screen
    dispatchToReducer({ type: 'initialize' });
    let scroll = horizontalScroll;

    // When merging clusters the scroll width gets reduced
    // so we need to handle this edge case exclusively in order to
    // keep the web-gl transition consistent with the browser scroll.
    if (
      oldClusters.length > clusters.length &&
      filterOption === oldFilter &&
      scrollElement != null
    ) {
      const maxScroll = scrollElement.scrollWidth - scrollElement.clientWidth;
      const removedClustersNumber = oldClusters.length - clusters.length;
      if (
        horizontalScroll >
        maxScroll - removedClustersNumber * CLUSTER_WIDTH
      ) {
        scroll =
          maxScroll - removedClustersNumber * CLUSTER_WIDTH > 0
            ? maxScroll - removedClustersNumber * CLUSTER_WIDTH
            : 0;
        scrollElement.scrollLeft = scroll;
        setHorizontalScroll(scroll);
      }
    }
    setOldClusters(clusters);

    // when filtering clusters the number of displayed clusters
    // changes so the scroll should be reset to align with the
    // web-gl transitions
    if (filterOption !== oldFilter && scrollElement != null) {
      scroll = 0;
      scrollElement.scrollLeft = scroll;
      setHorizontalScroll(scroll);
      setOldFilter(filterOption);
    }

    return clusters.map((cluster) => (
      <BaseCluster
        key={cluster.id}
        cluster={cluster}
        ref={(node) =>
          measureDimension(
            node,
            cluster,
            xMax,
            { x: -scroll, y: 0 },
            dispatchToReducer,
          )
        }
      />
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusters, xMax]);

  const onWheel = useCallback((event: WheelEvent<HTMLElement>) => {
    event.stopPropagation();
    if (event.currentTarget != null) {
      event.currentTarget.scrollLeft += -event.deltaY;
    }
  }, []);

  const onScroll = useCallback(
    (event: UIEvent<HTMLElement, globalThis.UIEvent>) => {
      setHorizontalScroll(event.currentTarget.scrollLeft);
    },
    [],
  );

  return (
    <VStack
      spacing='s'
      alignItems='stretch'
      bgColor='neutral.900'
      width='100vw'
      position={'relative'}
    >
      <BaseClustersHeader>{children}</BaseClustersHeader>
      <Box
        width='100vw'
        height='100%'
        overflow='auto'
        minHeight={MIN_HEIGHT}
        ref={setScrollElement}
        padding={`0 0 0 ${PADDING}px`}
        whiteSpace='nowrap'
        style={{ overscrollBehavior: 'none' }}
        onWheel={onWheel}
        onScroll={onScroll}
      >
        <WebGLContainer
          width={CONTAINER_WIDTH}
          height={CONTAINER_HEIGHT}
          canvasWidth={WIDTH}
          canvasHeight={CONTAINER_HEIGHT}
          clusters={clusters}
          style={{
            position: 'absolute',
            transform: `translateX(-${PADDING}px)`,
          }}
          xAxis={{ xMax }}
          yAxis={{ yMin, yMax }}
          scroll={{ x: -horizontalScroll, y: 0 }}
          dimensions={dimensions}
        />
        {memoisedBaseClusters}
      </Box>
    </VStack>
  );
};
